import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export type ActivityEventType =
  | 'loan_created'
  | 'loan_returned'
  | 'item_added'
  | 'item_giving_away'
  | 'request_received'
  | 'request_approved'
  | 'request_rejected';

export interface ActivityEvent {
  id: string;
  type: ActivityEventType;
  text: string;
  actorName: string;
  targetName?: string;
  itemName: string;
  timestamp: string;
}

async function fetchGroupActivity(groupId: string, limit: number): Promise<ActivityEvent[]> {
  // Step 1: Get item IDs + item_groups rows for this group
  const { data: itemGroupRows, error: igError } = await supabase
    .from('item_groups')
    .select('id, item_id, created_at')
    .eq('group_id', groupId);

  if (igError) throw igError;
  if (!itemGroupRows || itemGroupRows.length === 0) return [];

  const itemIds = itemGroupRows.map((r) => r.item_id);

  // Step 2: Fetch loans for these items (all statuses)
  const { data: loans, error: loansError } = await supabase
    .from('loans')
    .select('id, item_id, borrower_id, lender_user_id, borrowed_at, returned_at, status, group_id')
    .in('item_id', itemIds);

  if (loansError) throw loansError;

  // Step 3: Fetch loan requests for these items
  const { data: loanRequests, error: lrError } = await supabase
    .from('loan_requests')
    .select('id, item_id, requester_id, approver_id, status, created_at, responded_at')
    .in('item_id', itemIds);

  if (lrError) throw lrError;

  // Step 4: Fetch items for names + owner IDs + giving-away state
  const { data: items, error: itemsError } = await supabase
    .from('items')
    .select('id, name, owner_id, is_giving_away, giving_away_set_at')
    .in('id', itemIds);

  if (itemsError) throw itemsError;

  // [LEARN] Building a lookup object keyed by ID gives O(1) lookups later, much faster than
  // calling array.find() repeatedly when processing loans and requests below.
  const itemMap: Record<string, { name: string; owner_id: string; is_giving_away: boolean; giving_away_set_at: string | null }> = {};
  for (const item of items ?? []) {
    itemMap[item.id] = {
      name: item.name,
      owner_id: item.owner_id,
      is_giving_away: item.is_giving_away ?? false,
      giving_away_set_at: item.giving_away_set_at ?? null,
    };
  }

  // Step 5: Collect all user IDs and fetch profiles
  const userIdSet = new Set<string>();
  for (const loan of loans ?? []) {
    if (loan.borrower_id) userIdSet.add(loan.borrower_id);
    if (loan.lender_user_id) userIdSet.add(loan.lender_user_id);
  }
  for (const req of loanRequests ?? []) {
    if (req.requester_id) userIdSet.add(req.requester_id);
    if (req.approver_id) userIdSet.add(req.approver_id);
  }
  for (const item of items ?? []) {
    if (item.owner_id) userIdSet.add(item.owner_id);
  }

  const userIds = Array.from(userIdSet);
  const profileMap: Record<string, string> = {};

  if (userIds.length > 0) {
    const { data: profiles, error: profilesError } = await supabase
      .from('profiles')
      .select('user_id, display_name')
      .in('user_id', userIds);

    if (profilesError) throw profilesError;

    for (const profile of profiles ?? []) {
      profileMap[profile.user_id] = profile.display_name ?? 'Someone';
    }
  }

  const name = (userId: string | null) => (userId ? (profileMap[userId] ?? 'Someone') : 'Someone');

  const events: ActivityEvent[] = [];

  // Loan created + loan returned events
  // Only show loans that originated in this group (or have no group_id for backwards compat)
  for (const loan of loans ?? []) {
    const item = itemMap[loan.item_id];
    if (!item) continue;

    // Hide cross-group loans: if loan has a group_id that doesn't match, skip it
    if (loan.group_id && loan.group_id !== groupId) continue;

    events.push({
      id: `loan_created_${loan.id}`,
      type: 'loan_created',
      actorName: name(loan.borrower_id),
      targetName: name(loan.lender_user_id),
      itemName: item.name,
      text: `${name(loan.borrower_id)} borrowed ${item.name} from ${name(loan.lender_user_id)}`,
      timestamp: loan.borrowed_at,
    });

    if (loan.status === 'RETURNED' && loan.returned_at) {
      events.push({
        id: `loan_returned_${loan.id}`,
        type: 'loan_returned',
        actorName: name(loan.borrower_id),
        itemName: item.name,
        text: `${name(loan.borrower_id)} returned ${item.name} to ${name(item.owner_id)}`,
        timestamp: loan.returned_at,
      });
    }
  }

  // Item added to group events
  for (const ig of itemGroupRows) {
    const item = itemMap[ig.item_id];
    if (!item) continue;

    events.push({
      id: `item_added_${ig.id}`,
      type: 'item_added',
      actorName: name(item.owner_id),
      itemName: item.name,
      text: `${name(item.owner_id)} added ${item.name} to the group`,
      timestamp: ig.created_at,
    });
  }

  // Item giving-away events
  // One event per item_groups row for items currently marked as giving away.
  // giving_away_set_at is set by DB trigger when is_giving_away transitions
  // FALSE → TRUE, so it is always non-null when is_giving_away is true.
  for (const ig of itemGroupRows) {
    const item = itemMap[ig.item_id];
    if (!item) continue;
    if (!item.is_giving_away || !item.giving_away_set_at) continue;

    events.push({
      id: `item_giving_away_${ig.id}`,
      type: 'item_giving_away',
      actorName: name(item.owner_id),
      itemName: item.name,
      text: `${name(item.owner_id)} is giving away ${item.name}`,
      timestamp: item.giving_away_set_at,
    });
  }

  // Loan request events
  for (const req of loanRequests ?? []) {
    const item = itemMap[req.item_id];
    if (!item) continue;

    // Request received (always emitted)
    events.push({
      id: `request_received_${req.id}`,
      type: 'request_received',
      actorName: name(req.requester_id),
      itemName: item.name,
      text: `${name(req.requester_id)} requested to borrow ${item.name}`,
      timestamp: req.created_at,
    });

    // Request approved
    if (req.status === 'APPROVED' && req.responded_at) {
      events.push({
        id: `request_approved_${req.id}`,
        type: 'request_approved',
        actorName: name(req.approver_id),
        itemName: item.name,
        text: `${name(req.approver_id)} approved ${name(req.requester_id)}'s request for ${item.name}`,
        timestamp: req.responded_at,
      });
    }

    // Request rejected
    if (req.status === 'REJECTED' && req.responded_at) {
      events.push({
        id: `request_rejected_${req.id}`,
        type: 'request_rejected',
        actorName: name(req.approver_id),
        itemName: item.name,
        text: `${name(req.approver_id)} declined ${name(req.requester_id)}'s request for ${item.name}`,
        timestamp: req.responded_at,
      });
    }
  }

  // Sort newest first, apply limit.
  // Note: pagination is client-side — all events are fetched then sliced.
  // This is fine for typical group sizes but may need DB-level pagination if groups grow large.
  events.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
  return events.slice(0, limit);
}

/**
 * Returns a TanStack Query for the activity feed of a single group.
 * Aggregates loans, loan requests, and item-added events for items linked to
 * the group. Cross-group loans (where `loan.group_id` differs from the
 * requested group) are excluded to preserve loan-visibility boundaries.
 * Results are sorted newest-first and capped at the given limit.
 * @param groupId - The UUID of the group whose activity feed to fetch.
 * @param limit - Maximum number of events to return (default 8).
 * @param options - Optional query behaviour overrides.
 * @param options.usePreviousData - When true, keeps previous results visible while fetching a new page (useful for pagination).
 * @returns A TanStack `UseQueryResult` resolving to an array of {@link ActivityEvent} objects.
 */
export function useGroupActivity(
  groupId: string,
  limit = 8,
  options?: { usePreviousData?: boolean },
) {
  return useQuery({
    queryKey: ['group-activity', groupId, limit],
    queryFn: () => fetchGroupActivity(groupId, limit),
    enabled: !!groupId,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: true,
    placeholderData: options?.usePreviousData ? keepPreviousData : undefined,
  });
}
